import React, { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { intComma, pluralize } from "humanize-plus";
import sort from "fast-sort";

import Histories from "./Histories";
import {
  hoursLeft,
  payoutToOdds,
  PropWinners,
  oddsToString,
  getBetCapacity
} from "../utilities";
import ExternalLinkIcon from "../svg/ExternalLinkIcon";
import { Bet, OptionData, Proposition } from "../shared/interfaces";
import LockIcon from "../svg/LockIcon";
import TimeIcon from "../svg/TimeIcon";
import { PCT_MAX } from "../shared/constants";
import variables from "../styleVariables";

interface Props {
  existingBets: Array<Bet>;
  existingTotal: number;
  favorite: OptionData;
  loggedIn: boolean;
  proposition: Proposition;
  availablePoints: number;
}

const PropositionMeta = ({
  availablePoints,
  existingBets,
  existingTotal,
  favorite,
  loggedIn,
  proposition
}: Props) => {
  const [showCapExplainer, setShowCapExplainer] = useState(false);

  let netGain = 0;

  if (proposition.status === 4) {
    existingBets.map(bet => {
      if (bet.status === 4) {
        netGain -= bet.amount;
      } else if (bet.status === 5) {
        netGain += bet.profit;
      }
      return true;
    });
  }

  const betCapacity = getBetCapacity(proposition.action, existingTotal);

  return (
    <aside className="proposition__meta">
      {proposition.memo && (
        <Fragment>
          <h6>Special note</h6>
          <p>{proposition.memo}</p>
        </Fragment>
      )}

      <h6>Status</h6>
      <PropStatus proposition={proposition} loggedIn={loggedIn} />

      {proposition.type === 3 && proposition.status !== 4 && (
        <p>
          This is a show proposition. This means that a rider will pay out for
          finishing first, second, or third. You may win more than once within
          this proposition.
        </p>
      )}
      {proposition.bonus && (
        <p>
          This proposition {proposition.coming ? "has" : "had"} a bonus of{" "}
          <strong className="nobr">{proposition.bonus} percent</strong>.
        </p>
      )}

      {proposition.status === 4 && existingBets.length > 0 && (
        <PlayerOutcome netGain={netGain} />
      )}

      {proposition.winners.length > 0 && (
        <Fragment>
          <h6>Biggest winners</h6>
          <ul className="winner-loser-list">
            {proposition.winners.slice(0, 5).map((winner, i) => (
              <WinnerLoser outcome={winner} key={i} index={i} />
            ))}
          </ul>
        </Fragment>
      )}
      {proposition.losers.length > 0 && (
        <Fragment>
          <h6>Biggest losers</h6>
          <ul className="winner-loser-list">
            {proposition.losers.slice(0, 5).map((loser, i) => (
              <WinnerLoser outcome={loser} key={i} index={i} />
            ))}
          </ul>
        </Fragment>
      )}

      {proposition.contest && (
        <Fragment>
          <h6>Contest</h6>
          <Link to={`/contest/${proposition.contest.slug}`}>
            {proposition.contest.name}
          </Link>
        </Fragment>
      )}
      {proposition.location && (
        <div className="proposition__meta-row">
          <div className="proposition__meta-col">
            <h6>Location</h6>
            <p>{proposition.location}</p>
          </div>
        </div>
      )}
      <div className="proposition__meta-row">
        <div className="proposition__meta-col">
          <h6>Date</h6>
          <p>{proposition.date_string}</p>
        </div>

        {proposition.coming && (
          <div className="proposition__meta-col">
            <h6>Time left to bet</h6>
            <p className="proposition__meta-p--with-icon">
              <TimeIcon />
              {hoursLeft(proposition.hours_left)}
            </p>
          </div>
        )}
      </div>
      {proposition.hours_since_recalculation && (
        <Fragment>
          <h6>Last update to odds</h6>
          <p>{formatRecalc(proposition.hours_since_recalculation)}</p>
        </Fragment>
      )}

      {(proposition.course_profile || proposition.startlist) && (
        <div className="proposition__meta-row">
          {proposition.startlist && (
            <div className="proposition__meta-col">
              <h6>Start list</h6>
              <p className="proposition__meta-p--with-icon">
                <a
                  href={proposition.startlist}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLinkIcon color={variables.colorPrimary} />
                  Visit
                </a>
              </p>
            </div>
          )}
          {proposition.course_profile && (
            <div className="proposition__meta-col">
              <h6>Course Profile</h6>
              <p className="proposition__meta-p--with-icon">
                <a
                  href={proposition.course_profile}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLinkIcon color={variables.colorPrimary} />
                  Visit
                </a>
              </p>
            </div>
          )}
        </div>
      )}

      {loggedIn && proposition.status === 2 ? (
        <Fragment>
          <div className="proposition__meta-row">
            <div className="proposition__meta-col">
              <CurrentAction proposition={proposition} />
            </div>
            <div className="proposition__meta-col">
              <h6>Your action</h6>
              <PlayerAction existingBets={existingBets} />
            </div>
          </div>
          {proposition.coming && (
            <Fragment>
              <h6>How much you may currently bet</h6>

              <AvailableToBet
                betCapacity={betCapacity}
                availablePoints={availablePoints}
                showCapExplainer={showCapExplainer}
                setShowCapExplainer={setShowCapExplainer}
                PCT_MAX={PCT_MAX}
              />
            </Fragment>
          )}
        </Fragment>
      ) : (
        <CurrentAction proposition={proposition} />
      )}
      {proposition.type !== 3 &&
        proposition.status === 2 &&
        existingBets.length > 0 && (
          <Fragment>
            <h6>Best-case scenario</h6>
            <BestCaseScenario
              options={proposition.options}
              existingBets={existingBets}
            />
          </Fragment>
        )}

      <Histories
        winner={proposition.winner}
        favorite={favorite}
        options={proposition.options.filter(option => option.scratch === false)}
        mobile={false}
      />

      {proposition.race && proposition.race.related_propositions.length > 1 && (
        <div className="proposition__related">
          <h6>Related propositions</h6>
          <ul>
            {proposition.race.related_propositions
              .filter(
                related_proposition =>
                  proposition.question !== related_proposition.question
              )
              .sort((a, b) => a.hours_left < b.hours_left)
              .sort((a, b) => (a.coming === b.coming ? 0 : b.coming ? -1 : 1))
              .map((proposition, i) => (
                <RelatedItem key={i} proposition={proposition} />
              ))}
          </ul>
        </div>
      )}
    </aside>
  );
};

const AvailableToBet = ({
  betCapacity,
  availablePoints,
  showCapExplainer,
  setShowCapExplainer,
  PCT_MAX
}) => {
  if (availablePoints < betCapacity) {
    return (
      <Fragment>
        <p>
          {intComma(availablePoints)} {pluralize(availablePoints, "point")}
        </p>
        {showCapExplainer ? (
          <p>You may not bet more points than you currently have.</p>
        ) : (
          <p className="link" onClick={() => setShowCapExplainer(true)}>
            Read why
          </p>
        )}
      </Fragment>
    );
  }

  if (betCapacity > 0) {
    return (
      <Fragment>
        <p>
          {intComma(betCapacity)} {pluralize(betCapacity, "point")}
        </p>
        {showCapExplainer ? (
          <p>
            A single player’s bets may not constitute more than {PCT_MAX}{" "}
            percent of a proposition’s action.
          </p>
        ) : (
          <p>
            <button
              className="button--inline button--no-underline"
              onClick={() => setShowCapExplainer(true)}
            >
              Read why
            </button>
          </p>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <p>You are currently maxed out on this proposition.</p>
      {showCapExplainer ? (
        <p>
          A single player’s bets may not constitute more than {PCT_MAX} percent
          of a proposition’s action. To bet more, you must wait for more players
          to make their moves.
        </p>
      ) : (
        <p>
          <button
            className="button--inline button--no-underline"
            onClick={() => setShowCapExplainer(true)}
          >
            Read why
          </button>
        </p>
      )}
    </Fragment>
  );
};

const BestCaseScenario = ({ existingBets, options }) => {
  const betTotal = existingBets.reduce((a, b) => ({
    amount: parseInt(a.amount || 0) + parseInt(b.amount || 0)
  })).amount;

  var bestOption: any | null = null;
  var bestBetNet = 0;
  options.map(option => {
    let thisOptionPotentialGross = 0;
    let correspondingBets = existingBets.filter(bet => option.option.id === bet.selection.id);
    if (correspondingBets.length > 0) {
      correspondingBets.map(bet => {
        const odds = payoutToOdds(bet.pre_payout);
        thisOptionPotentialGross =
          thisOptionPotentialGross +
          (odds[0] / odds[1]) * bet.amount +
          bet.amount;
      });

      if (thisOptionPotentialGross < 1) {
        thisOptionPotentialGross = 1;
      }
      let totalPotentialNet = Math.round(thisOptionPotentialGross - betTotal);
      if (totalPotentialNet > bestBetNet) {
        bestBetNet = totalPotentialNet;
        bestOption = option;
      }
    }
    return true;
  });
  if (bestOption && bestOption.option) {
    return (
      <p>
        You would net {intComma(bestBetNet)} {pluralize(bestBetNet, "point")} if{" "}
        {bestOption.option.name} were to win this proposition.
      </p>
    );
  } else {
    return <p>There is no way for you to net points on this proposition.</p>;
  }
};

const CurrentAction = ({ proposition }) => (
    <Fragment>
      <h6>{proposition.coming ? "Current" : "Final"} action</h6>
      <p>{formatCurrentAction(proposition.action)}</p>
    </Fragment>
  );

const formatCurrentAction = (action: number): string => {
  if (action < 500) {
    return "Less than 500 points";
  } else {
    return `${intComma(roundToTwoSigFigs(action))} points`;
  }
};

const formatRecalc = (hours: number): string => {
  if (hours < 0.2) {
    return "Just now";
  }
  if (hours < 0.5) {
    return "Less than 30 minutes ago";
  }
  if (hours < 1.5) {
    return "About an hour ago";
  }
  if (hours < 12) {
    return `About ${Math.round(hours)} hours ago`;
  }
  const days = Math.round(hours / 24);
  return `About ${days} ${pluralize(days, "day")} ago`;
};

const PlayerAction = ({ existingBets }) => {
  if (existingBets.length === 0) {
    return <p>0</p>;
  }
  const betTotal = existingBets.reduce((a, b) => ({
    amount: parseInt(a.amount || 0) + parseInt(b.amount || 0)
  })).amount;

  return <p>{intComma(betTotal)} points</p>;
};

const PlayerOutcome = ({ netGain }): any => {
  if (netGain === 0) {
    return (
      <Fragment>
        <h6>Your outcome</h6>
        <p>You broke even on this proposition.</p>
      </Fragment>
    );
  }
  if (netGain > 0) {
    return (
      <Fragment>
        <h6>Your outcome</h6>
        <p>
          In total, you came out {intComma(netGain)}{" "}
          {pluralize(netGain, "point")} ahead on this proposition.
        </p>
      </Fragment>
    );
  }

  if (netGain < 0) {
    netGain = netGain * -1;
    return (
      <Fragment>
        <h6>Your outcome</h6>
        <p>
          In total, you lost {intComma(netGain)} {pluralize(netGain, "point")}{" "}
          on this proposition.
        </p>
      </Fragment>
    );
  }
};

const PropStatus = ({ proposition, loggedIn }) => {
  if (proposition.status === 1) {
    return (
      <p>
        <LockIcon /> Betting has not opened yet.
      </p>
    );
  } else if (proposition.status === 2 && proposition.coming) {
    return (
      <p>
        Open for betting
        {!loggedIn && (
          <span>
            , but you must{" "}
            <Link className="link--underline" to="/rd/login">
              log in
            </Link>{" "}
            or{" "}
            <Link className="link--underline" to="/rd/new">
              create an account
            </Link>{" "}
            to play
          </span>
        )}
        .
      </p>
    );
  } else if (proposition.status === 2 && !proposition.coming) {
    return (
      <p>
        <LockIcon /> Betting has closed.
      </p>
    );
  } else if (proposition.status === 4) {
    return (
      <p>
        Bets have been paid out.{" "}
        <PropWinners verbose={true} proposition={proposition} />
      </p>
    );
  } else {
    return <p>Unknown.</p>;
  }
};

const RelatedItem = ({ proposition }) => (
    <li className="proposition__related-races-item">
      {proposition.hours_left > 0 ? "" : <LockIcon />}
      <Link to={`/prop/${proposition.slug}`}>{proposition.question}</Link>
    </li>
  );

const roundToTwoSigFigs = (i: number): number => {
  // Sort of ... some fine-tuning for action less than 100
  if (i <= 10) {
    return i;
  }
  if (i <= 100) {
    return Math.round(i / 10) * 10;
  }

  const digits = i.toString().length;
  const precision = Math.pow(10, digits - 2);
  return Math.round(i / precision) * precision;
};

const WinnerLoser = ({ outcome, index }) => {
  const [showBets, setShowBets] = useState(false);

  const bets = sort(outcome.bets.filter(bet => bet.status !== 3)).by([
    { desc: b => b.status },
    { asc: b => b.selection.name },
    { desc: b => b.amount }
  ]);

  return (
    <li className="player__list-item winner-loser">
      <span className="player__list-item-rank">{index + 1}. </span>
      <span className="player__list-item-text">
        <Link to={`/${outcome.player.handle}`}>
          {outcome.player.screen_name}
        </Link>{" "}
        {bets.length > 0 &&
          <button
            className="button--inline button-no-underline winner-loser__show-more"
            onClick={() => setShowBets(!showBets)}
          >
            {showBets ? "-" : "+"}
          </button>
        }
      </span>
      <span className="player__list-item-figure">{intComma(outcome.net)}</span>
      {showBets && (
        <ul className="winner-loser__bet-list">
          {bets.map((bet: Bet, i: number) => (
            <li
              key={i}
              className={`winner-loser__bet-item ${
                bet.profit > 0 ? "winner-loser__bet-item--winner" : ""
              }`}
            >
              {intComma(bet.amount)} on {bet.selection.name} (
              {oddsToString(bet.odds)})
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default PropositionMeta;
